/**
 * ThinkingIndicator — animated status display during agent processing.
 * Shows thinking dots + status text (e.g., "Calling query_database...")
 * Also exports ReasoningAccordion — the collapsed-by-default live view of
 * Claude Sonnet 5's streamed reasoning (never persisted, streaming-only).
 */
import { Box, Typography, Collapse } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { MoftakiBotIcon } from '../../components/shared/MoftakiBotIcon';

interface Props {
  status: string;
}

export const ThinkingIndicator = ({ status }: Props) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5, px: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 0.5 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          backgroundColor: alpha(theme.chat.botAccent, 0.1),
        }}>
          <MoftakiBotIcon size={18} />
        </Box>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.5, py: 0.75, borderRadius: 2,
          backgroundColor: alpha(theme.palette.divider, 0.1),
        }}>
          <ThinkingDots />
          <Typography variant="caption" sx={{
            fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic',
          }}>
            {status}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

interface ReasoningProps {
  text: string;
  open: boolean;
  onToggle: () => void;
  /** true while reasoning is still streaming in (shows the pulsing dots) */
  live: boolean;
}

/**
 * ReasoningAccordion — subtle, collapsed-by-default "Reasoning…" strip shown
 * above the assistant message while Sonnet 5 streams its thinking. Auto-collapsed
 * by the parent when the visible answer starts streaming.
 */
export const ReasoningAccordion = ({ text, open, onToggle, live }: ReasoningProps) => {
  const theme = useTheme();
  if (!text) return null;

  return (
    <Box sx={{ px: 1, mb: 1, ml: 0.5, maxWidth: '85%' }}>
      <Box onClick={onToggle} sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
        px: 1.25, py: 0.5, borderRadius: 1.5,
        backgroundColor: alpha(theme.palette.divider, 0.08),
        '&:hover': { backgroundColor: alpha(theme.palette.divider, 0.16) },
      }}>
        {live && <ThinkingDots />}
        <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary', fontStyle: 'italic' }}>
          {live ? 'Reasoning…' : 'Reasoning'}
        </Typography>
        <ExpandMore sx={{
          fontSize: 15, color: 'text.secondary',
          transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s',
        }} />
      </Box>
      <Collapse in={open}>
        <Box sx={{
          mt: 0.5, px: 1.5, py: 1, borderRadius: 1.5, maxHeight: 180, overflowY: 'auto',
          backgroundColor: alpha(theme.palette.divider, 0.06),
          borderLeft: `2px solid ${alpha(theme.palette.text.secondary, 0.25)}`,
        }}>
          <Typography variant="caption" sx={{
            fontSize: '0.72rem', color: 'text.secondary', fontStyle: 'italic',
            whiteSpace: 'pre-wrap', display: 'block', lineHeight: 1.5,
          }}>
            {text}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

/** Three bouncing dots animation */
function ThinkingDots() {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
      {[0, 1, 2].map((i) => (
        <Box key={i} sx={{
          width: 5, height: 5, borderRadius: '50%',
          backgroundColor: theme.chat.botAccent,
          animation: 'thinking-bounce 1.2s infinite ease-in-out',
          animationDelay: `${i * 0.15}s`,
          '@keyframes thinking-bounce': {
            '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
            '40%': { opacity: 1, transform: 'scale(1)' },
          },
        }} />
      ))}
    </Box>
  );
}
